import { useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { staffApi } from '../../../api/staff'
import type { AuthSession } from '../../../types/plans'
import { PatientSearchInput } from './PatientSearchInput'
import type { PatientSearchResult } from './PatientSearchInput'

export function CreatePlanModal({
  bootstrap,
  session,
  onCreated,
  onClose,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  onCreated: (planId: number) => void
  onClose: () => void
}) {
  const [selectedPatient, setSelectedPatient] = useState<PatientSearchResult | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function handleCreate() {
    if (!selectedPatient) { setError('Выберите пациента из списка'); return }
    if (!title.trim()) { setError('Введите название плана'); return }

    setSaving(true)
    setError('')
    try {
      const res = await staffApi.createConstructorPlan(bootstrap, session, {
        patient_id: selectedPatient.id,
        patient_name: selectedPatient.full_name,
        title: title.trim(),
        description: description.trim(),
      }) as { plan_id?: number; success?: boolean }
      if (res.plan_id) onCreated(res.plan_id)
      else onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Ошибка создания плана')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">Создать план лечения</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Пациент</label>
            <PatientSearchInput
              bootstrap={bootstrap}
              session={session}
              selectedPatient={selectedPatient}
              onSelect={setSelectedPatient}
            />
            {selectedPatient && (
              <p className="mt-1 text-[11px] text-slate-400">ID: {selectedPatient.id}</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Название плана</label>
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Например: Комплексное лечение"
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Описание (необязательно)</label>
            <textarea
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              rows={2}
              placeholder="Краткое описание плана"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>
        </div>

        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

        <div className="mt-5 flex gap-3">
          <button
            type="button"
            className="flex-1 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            onClick={() => void handleCreate()}
            disabled={saving}
          >
            {saving ? 'Создание...' : 'Создать план'}
          </button>
          <button
            type="button"
            className="rounded-lg bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-200"
            onClick={onClose}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  )
}
